import { Autocomplete } from "@mui/material";
import { FormikValues, getIn } from "formik";

import { getFieldPath } from "../FormikField.utils";
import { FormikAutocompleteProps } from "./FormikAutocomplete.types";
import { FormikAutocompleteTextField } from "./components/FormikAutocompleteTextField";
import { useStickyStaleOptions } from "./hooks/useStickyStaleOptions";

export function FormikAutocomplete<T extends FormikValues, FreeSolo extends boolean>(
  props: FormikAutocompleteProps<T, false, FreeSolo>
) {
  const {
    fieldName,
    path,
    textFieldProps,
    formik,
    helperText,
    label,
    options,
    freeSolo,
    required,
    optional,
    onValueChange,
    clearValue = null,
    ...others
  } = props;

  const fullPath = getFieldPath(path, fieldName);
  const formValue = (getIn(formik.values, fullPath) as string | null | undefined) ?? null;
  const initialValue = (getIn(formik.initialValues, fullPath) as string | null | undefined) ?? null;

  const mergedOptions = useStickyStaleOptions(options, initialValue);

  return (
    <Autocomplete<string, false, false, FreeSolo>
      freeSolo={freeSolo}
      value={formValue}
      inputValue={freeSolo ? (formValue ?? "") : undefined}
      onChange={(_, newValue, reason) => {
        if (reason === "clear") {
          void formik.setFieldValue(fullPath, clearValue);
          onValueChange?.(clearValue);
        } else {
          void formik.setFieldValue(fullPath, newValue);
          onValueChange?.(newValue);
        }
      }}
      onInputChange={(_, newValue, reason) => {
        if (freeSolo && reason === "input") {
          void formik.setFieldValue(fullPath, newValue);
          onValueChange?.(newValue);
        }
      }}
      isOptionEqualToValue={(option, val) => option === val}
      getOptionLabel={(option) => option}
      options={mergedOptions}
      renderInput={(params) => (
        <FormikAutocompleteTextField
          params={params}
          textFieldProps={textFieldProps}
          formik={formik}
          fieldPath={fullPath}
          helperText={helperText}
          label={label}
          required={required}
          optional={optional}
        />
      )}
      fullWidth
      {...others}
    />
  );
}
